import { useCallback, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  toast
} from '@etherisc/ui-kit';
import { PlusIcon, TrashIcon, AlertTriangleIcon } from 'lucide-react';
import { EditableCell } from './EditableCell';
import { OrganizationSelector, type Organization } from './OrganizationSelector';
import type { StakeholdersAnrpDistribution } from '../../types/product';

interface StakeholdersTableProps {
  stakeholders: StakeholdersAnrpDistribution;
  onChange: (stakeholders: StakeholdersAnrpDistribution) => void;
  availableOrganizations: Organization[];
  errors?: Array<{
    path: string;
    message: string;
    value: unknown;
  }>;
  readOnly?: boolean;
}

interface FlattenedStakeholder {
  id: string;
  type: 'administration' | 'underwriter' | 'broker' | 'calculating-agent' | 'other';
  subtype?: 'insurer' | 'myubuntu';
  organizationId: string;
  name: string;
  percentage: number;
  role?: string;
  isInsurer?: boolean;
  isEditable: boolean;
}

export function StakeholdersTable({
  stakeholders,
  onChange,
  availableOrganizations,
  errors = [],
  readOnly = false
}: StakeholdersTableProps) {

  
  // Flatten stakeholders into a single array for table display
  const flattenedStakeholders = useMemo((): FlattenedStakeholder[] => {
    const result: FlattenedStakeholder[] = [];

    // Administration stakeholders (editable percentages and organization selection)
    if (stakeholders?.administration?.insurer) {
      result.push({
        id: 'admin-insurer',
        type: 'administration',
        subtype: 'insurer',
        organizationId: stakeholders.administration.insurer.organizationId,
        name: stakeholders.administration.insurer.name,
        percentage: stakeholders.administration.insurer.percentage,
        role: stakeholders.administration.insurer.role,
        isEditable: true // Make insurer editable
      });
    }

    if (stakeholders?.administration?.myubuntu) {
      result.push({
        id: 'admin-myubuntu',
        type: 'administration',
        subtype: 'myubuntu',
        organizationId: stakeholders.administration.myubuntu.organizationId,
        name: stakeholders.administration.myubuntu.name,
        percentage: stakeholders.administration.myubuntu.percentage,
        role: stakeholders.administration.myubuntu.role,
        isEditable: true // Make myubuntu editable (percentage only, not organization)
      });
    }

    // Underwriters (editable)
    stakeholders?.underwriters?.forEach((u, i) => {
      result.push({
        id: `underwriter-${i}`,
        type: 'underwriter',
        organizationId: u.organizationId,
        name: u.name,
        percentage: u.percentage,
        isInsurer: u.isInsurer,
        isEditable: true
      });
    });

    // Brokers (editable)
    stakeholders?.brokers?.forEach((b, i) => {
      result.push({
        id: `broker-${i}`,
        type: 'broker',
        organizationId: b.organizationId,
        name: b.name,
        percentage: b.percentage,
        role: b.role,
        isEditable: true
      });
    });

    // Calculating Agents (editable)
    stakeholders?.calculatingAgents?.forEach((c, i) => {
      result.push({
        id: `calc-agent-${i}`,
        type: 'calculating-agent',
        organizationId: c.organizationId,
        name: c.name,
        percentage: c.percentage,
        role: c.role,
        isEditable: true
      });
    });

    // Other Organizations (editable, no percentage)
    stakeholders?.otherOrganisations?.forEach((o, i) => {
      result.push({
        id: `other-${i}`,
        type: 'other',
        organizationId: o.organizationId,
        name: o.name,
        percentage: 0, // Other orgs don't have percentages
        isEditable: true
      });
    });

    return result;
  }, [stakeholders]);

  // Calculate total percentage (excluding 'other' organizations)
  const totalPercentage = useMemo(() => {
    return flattenedStakeholders
      .filter(s => s.type !== 'other')
      .reduce((sum, s) => sum + s.percentage, 0);
  }, [flattenedStakeholders]);

  const isValidTotal = Math.abs(totalPercentage - 1.0) < 0.001;

  const updateStakeholderOrganization = useCallback((stakeholderId: string, orgId: string, orgName: string) => {
    const newStakeholders = { ...stakeholders };
    
    if (stakeholderId === 'admin-insurer' && newStakeholders.administration?.insurer) {
      newStakeholders.administration.insurer.organizationId = orgId;
      newStakeholders.administration.insurer.name = orgName;
    } else if (stakeholderId === 'admin-myubuntu' && newStakeholders.administration?.myubuntu) {
      newStakeholders.administration.myubuntu.organizationId = orgId;
      newStakeholders.administration.myubuntu.name = orgName;
    } else if (stakeholderId.startsWith('underwriter-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.underwriters?.[index]) {
        newStakeholders.underwriters[index].organizationId = orgId;
        newStakeholders.underwriters[index].name = orgName;
      }
    } else if (stakeholderId.startsWith('broker-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.brokers && newStakeholders.brokers[index]) {
        newStakeholders.brokers[index].organizationId = orgId;
        newStakeholders.brokers[index].name = orgName;
      }
    } else if (stakeholderId.startsWith('calc-agent-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.calculatingAgents && newStakeholders.calculatingAgents[index]) {
        newStakeholders.calculatingAgents[index].organizationId = orgId;
        newStakeholders.calculatingAgents[index].name = orgName;
      }
    } else if (stakeholderId.startsWith('other-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.otherOrganisations && newStakeholders.otherOrganisations[index]) {
        newStakeholders.otherOrganisations[index].organizationId = orgId;
        newStakeholders.otherOrganisations[index].name = orgName;
      }
    }

    onChange(newStakeholders);
  }, [stakeholders, onChange]);

  const updateStakeholderPercentage = useCallback((stakeholderId: string, percentage: number) => {
    const newStakeholders = { ...stakeholders };
    
    if (stakeholderId === 'admin-insurer' && newStakeholders.administration?.insurer) {
      newStakeholders.administration.insurer.percentage = percentage;
    } else if (stakeholderId === 'admin-myubuntu' && newStakeholders.administration?.myubuntu) {
      newStakeholders.administration.myubuntu.percentage = percentage;
    } else if (stakeholderId.startsWith('underwriter-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.underwriters?.[index]) {
        newStakeholders.underwriters[index].percentage = percentage;
      }
    } else if (stakeholderId.startsWith('broker-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.brokers && newStakeholders.brokers[index]) {
        newStakeholders.brokers[index].percentage = percentage;
      }
    } else if (stakeholderId.startsWith('calc-agent-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.calculatingAgents && newStakeholders.calculatingAgents[index]) {
        newStakeholders.calculatingAgents[index].percentage = percentage;
      }
    }

    onChange(newStakeholders);
  }, [stakeholders, onChange]);

  const addStakeholder = useCallback((type: string) => {
    const newStakeholders = { ...stakeholders };
    const placeholderOrgId = '00000000-0000-0000-0000-000000000000';

    switch (type) {
      case 'underwriter':
        if (!newStakeholders.underwriters) newStakeholders.underwriters = [];
        newStakeholders.underwriters.push({
          organizationId: placeholderOrgId,
          name: 'Select Organization',
          percentage: 0,
          isInsurer: false
        });
        break;
      case 'broker':
        if (!newStakeholders.brokers) newStakeholders.brokers = [];
        newStakeholders.brokers.push({
          organizationId: placeholderOrgId,
          name: 'Select Organization',
          percentage: 0,
          role: 'Broker'
        });
        break;
      case 'calculating-agent':
        if (!newStakeholders.calculatingAgents) newStakeholders.calculatingAgents = [];
        newStakeholders.calculatingAgents.push({
          organizationId: placeholderOrgId,
          name: 'Select Organization',
          percentage: 0,
          role: 'Calculating Agent'
        });
        break;
      case 'other':
        if (!newStakeholders.otherOrganisations) newStakeholders.otherOrganisations = [];
        newStakeholders.otherOrganisations.push({
          organizationId: placeholderOrgId,
          name: 'Select Organization',
          percentage: 0,
          role: 'Other'
        });
        break;
    }

    onChange(newStakeholders);
    toast.success(`Added new ${type.replace('-', ' ')}`);
  }, [stakeholders, onChange]);

  const deleteStakeholder = useCallback((stakeholderId: string) => {
    const newStakeholders = { ...stakeholders };

    if (stakeholderId.startsWith('underwriter-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.underwriters) {
        newStakeholders.underwriters.splice(index, 1);
      }
    } else if (stakeholderId.startsWith('broker-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.brokers) {
        newStakeholders.brokers.splice(index, 1);
      }
    } else if (stakeholderId.startsWith('calc-agent-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.calculatingAgents) {
        newStakeholders.calculatingAgents.splice(index, 1);
      }
    } else if (stakeholderId.startsWith('other-')) {
      const index = parseInt(stakeholderId.split('-')[1]);
      if (newStakeholders.otherOrganisations) {
        newStakeholders.otherOrganisations.splice(index, 1);
      }
    }

    onChange(newStakeholders);
    toast.success('Stakeholder removed');
  }, [stakeholders, onChange]);

  const getTypeDisplayName = (type: string, subtype?: string) => {
    if (type === 'administration') {
      return subtype === 'insurer' ? 'Admin (Insurer)' : 'Admin (MyUbuntu)';
    }
    return type.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      'administration': 'bg-blue-100 text-blue-800',
      'underwriter': 'bg-green-100 text-green-800',
      'broker': 'bg-yellow-100 text-yellow-800',
      'calculating-agent': 'bg-purple-100 text-purple-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.other;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-3">
            Stakeholders ANRP Distribution
            <Badge 
              variant={isValidTotal ? "default" : "destructive"}
              className="tabular-nums"
            >
              {(totalPercentage * 100).toFixed(1)}%
            </Badge>
          </CardTitle>
          {!readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <PlusIcon size={16} className="mr-2" />
                  Add Stakeholder
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => addStakeholder('underwriter')}>
                  Underwriter
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addStakeholder('broker')}>
                  Broker
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addStakeholder('calculating-agent')}>
                  Calculating Agent
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addStakeholder('other')}>
                  Other Organization
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <CardDescription>
          Configure ANRP (Adjusted Net Retained Premium) distribution among stakeholders. 
          Total must equal 100%.
          {flattenedStakeholders.length > 0 && (
            <span className="ml-2 text-sm">
              ({flattenedStakeholders.length} stakeholder{flattenedStakeholders.length !== 1 ? 's' : ''} configured)
            </span>
          )}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>ANRP %</TableHead>
                <TableHead>Role/Flags</TableHead>
                {!readOnly && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {flattenedStakeholders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={readOnly ? 4 : 5} className="text-center text-gray-500 py-8">
                    {readOnly 
                      ? "No stakeholders configured."
                      : "No stakeholders configured. Click \"Add Stakeholder\" to start."
                    }
                  </TableCell>
                </TableRow>
              ) : (
                flattenedStakeholders.map((stakeholder) => (
                  <TableRow key={stakeholder.id}>
                    <TableCell>
                      <Badge 
                        variant="outline"
                        className={getTypeBadgeColor(stakeholder.type)}
                      >
                        {getTypeDisplayName(stakeholder.type, stakeholder.subtype)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {stakeholder.subtype === 'myubuntu' || readOnly ? (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{stakeholder.name}</span>
                          {stakeholder.subtype === 'myubuntu' && (
                            <Badge variant="secondary" size="sm">Fixed</Badge>
                          )}
                        </div>
                      ) : (
                        <OrganizationSelector
                          value={stakeholder.organizationId}
                          name={stakeholder.name}
                          organizations={availableOrganizations}
                          onChange={(orgId, orgName) => 
                            updateStakeholderOrganization(stakeholder.id, orgId, orgName)
                          }
                          disabled={!stakeholder.isEditable}
                          className="min-w-[250px]"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {stakeholder.type !== 'other' ? (
                        <EditableCell
                          value={stakeholder.percentage * 100} // Convert to percentage for display
                          onChange={(newValue) => 
                            updateStakeholderPercentage(stakeholder.id, newValue / 100)
                          }
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          suffix="%"
                          className="w-20"
                          disabled={!stakeholder.isEditable || readOnly}
                        />
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {stakeholder.role && (
                          <Badge variant="secondary" size="sm">
                            {stakeholder.role}
                          </Badge>
                        )}
                        {stakeholder.isInsurer && (
                          <Badge variant="default" size="sm">
                            Insurer
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {!readOnly && (
                        <div className="flex gap-1">
                          {stakeholder.isEditable ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteStakeholder(stakeholder.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Remove stakeholder"
                            >
                              <TrashIcon size={14} />
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-500 px-2">Fixed</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Percentage Validation */}
        {!isValidTotal && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangleIcon size={16} />
              <span className="font-medium">
                Total ANRP percentage must equal 100%. 
                Current total: {(totalPercentage * 100).toFixed(1)}%
                {totalPercentage < 1.0 && ` (Missing: ${((1.0 - totalPercentage) * 100).toFixed(1)}%)`}
                {totalPercentage > 1.0 && ` (Excess: ${((totalPercentage - 1.0) * 100).toFixed(1)}%)`}
              </span>
            </div>
          </div>
        )}

        {/* Error Display */}
        {errors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-700">
              <div className="font-medium mb-2">Stakeholder Validation Errors:</div>
              <ul className="text-sm space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error.message}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
